import { useState, useCallback } from 'react';
import { enrollmentsAPI } from '../services/api';
import { LEFT_STATUS, getPrimaryEnrollment } from '../utils/studentEnrollmentStatus';
import { patchStudentEnrollmentStatus } from '../utils/studentEnrollmentStatusPatch';

/**
 * Shared enrollment status updates for Filter Students and Student Management.
 * @param {(updater: (prev: Array) => Array) => void} patchStudents - updates main student list
 * @param {{ patchSecondaryList?: (updater: (prev: Array) => Array) => void }} [options]
 */
export function useEnrollmentStatusUpdates(patchStudents, options = {}) {
  const { patchSecondaryList } = options;
  const [statusUpdateError, setStatusUpdateError] = useState(null);
  const [statusUpdatingIds, setStatusUpdatingIds] = useState(() => new Set());
  const [bulkStatusUpdating, setBulkStatusUpdating] = useState(false);

  const setStudentUpdating = useCallback((studentId, updating) => {
    setStatusUpdatingIds((prev) => {
      const next = new Set(prev);
      const key = String(studentId);
      if (updating) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const applyLocalPatch = useCallback(
    (studentId, enrollmentId, batchStatus) => {
      patchStudents((prev) =>
        patchStudentEnrollmentStatus(prev, studentId, enrollmentId, batchStatus)
      );
      if (patchSecondaryList) {
        patchSecondaryList((prev) =>
          patchStudentEnrollmentStatus(prev, studentId, enrollmentId, batchStatus)
        );
      }
    },
    [patchStudents, patchSecondaryList]
  );

  const handleEnrollmentStatusChange = useCallback(
    async (student, enrollment, batchStatus) => {
      if (!enrollment?.id || enrollment.batchStatus === batchStatus) return;

      const studentName =
        student.fullName ||
        `${student.firstName || ''} ${student.lastName || ''}`.trim() ||
        'this student';

      if (
        batchStatus === LEFT_STATUS &&
        !window.confirm(`Mark ${studentName} as "${LEFT_STATUS}"?`)
      ) {
        return;
      }

      const studentId = student.id || student._id;
      setStatusUpdateError(null);
      setStudentUpdating(studentId, true);

      try {
        await enrollmentsAPI.update(String(enrollment.id), { batchStatus });
        applyLocalPatch(studentId, enrollment.id, batchStatus);
      } catch (error) {
        console.error('Error updating enrollment status:', error);
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to update student status';
        setStatusUpdateError(msg);
      } finally {
        setStudentUpdating(studentId, false);
      }
    },
    [applyLocalPatch, setStudentUpdating]
  );

  const handleBulkMarkAsLeft = useCallback(
    async (selectedStudents) => {
      const targets = selectedStudents.filter((student) => {
        if (student.userType && student.userType !== 'student') return false;
        const enrollment = getPrimaryEnrollment(student);
        return enrollment?.id && enrollment.batchStatus !== LEFT_STATUS;
      });

      if (targets.length === 0) {
        window.alert('No selected students with an enrollment that can be marked as left.');
        return;
      }

      if (
        !window.confirm(
          `Mark ${targets.length} selected student${targets.length !== 1 ? 's' : ''} as "${LEFT_STATUS}"?`
        )
      ) {
        return;
      }

      setStatusUpdateError(null);
      setBulkStatusUpdating(true);

      const failures = [];

      for (const student of targets) {
        const enrollment = getPrimaryEnrollment(student);
        const studentId = student.id || student._id;
        setStudentUpdating(studentId, true);
        try {
          await enrollmentsAPI.update(String(enrollment.id), { batchStatus: LEFT_STATUS });
          applyLocalPatch(studentId, enrollment.id, LEFT_STATUS);
        } catch (error) {
          failures.push(student.fullName || studentId);
        } finally {
          setStudentUpdating(studentId, false);
        }
      }

      setBulkStatusUpdating(false);

      if (failures.length > 0) {
        setStatusUpdateError(
          `Could not update status for: ${failures.slice(0, 5).join(', ')}${
            failures.length > 5 ? ` and ${failures.length - 5} more` : ''
          }`
        );
      }
    },
    [applyLocalPatch, setStudentUpdating]
  );

  return {
    statusUpdateError,
    setStatusUpdateError,
    statusUpdatingIds,
    bulkStatusUpdating,
    handleEnrollmentStatusChange,
    handleBulkMarkAsLeft,
  };
}

export default useEnrollmentStatusUpdates;
